<template>
  <div>
    <base-header class="pb-6 pb-8 pt-5 pt-md-8 bg-gradient-success">
      <b-button variant="secondary" class="ml-2" @click="$router.push('/plantas')">
        <i class="fas fa-arrow-left"></i> {{ $t('volver') }}
      </b-button>

      <b-card-header class="border-0">
        <h3 class="mb-0">{{ $t('planta') }}: {{ planta.nombre }}</h3>
      </b-card-header>

      <b-form @submit.prevent="submitForm">
        <div class="d-flex justify-content-end">
          <b-button @click="cancelBtn()" :variant="!isEditMode ? 'info' : 'danger'">
            {{ !isEditMode ? $t('editar') : $t('cancelar') }}
          </b-button>

          <b-button v-if="isEditMode" :disabled="!isEditMode" type="submit" variant="primary" class="ml-2">
            Actualizar
          </b-button>
        </div>

        <b-row>
          <b-col md="2">
            <b-form-group label-for="Nombre">
              <template v-slot:label>
                {{ $t('nombre') }} <span v-if="isEditMode" class="text-danger">*</span>
              </template>
              <b-form-input id="nombre" :disabled="!isEditMode" v-model="planta.nombre" required
                placeholder="Ingrese nombre"></b-form-input>
            </b-form-group>
            <b-form-group>
              <template v-slot:label>
                {{ $t('direccion') }} <span v-if="isEditMode" class="text-danger">*</span>
              </template>
              <b-form-input id="rut" :disabled="!isEditMode" v-model="planta.direccion" required></b-form-input>
            </b-form-group>
          </b-col>

          <b-col md="2">
            <b-form-group label="Pais" label-for="paisID">
              <template v-slot:label>
                {{ $t('pais') }} <span v-if="isEditMode" class="text-danger">*</span>
              </template>
              <b-form-select id="paisID" :disabled="!isEditMode" v-model="planta.paisID" @change="onPaisChange"
                required>
                <option :value="null" disabled>{{ $t('seleccione_opcion') }}</option>
                <option v-for="option in paisOptions" :key="option.value" :value="option.value">
                  {{ option.text }}
                </option>
              </b-form-select>
            </b-form-group>
            <b-form-group>
              <template v-slot:label>
                {{ $t('empresa') }}
              </template>
              <b-form-select id="empresaID" :disabled="!isPaisSelected && !isEditMode" v-model="planta.empresaID">
                <option :value="null">{{ $t('seleccione_opcion') }}</option>
                <option v-for="option in empresaOptions" :key="option.value" :value="option.value">
                  {{ option.text }}
                </option>
              </b-form-select>
            </b-form-group>
          </b-col>

          <b-col md="2">
            <b-form-group label-for="regionID">
              <template v-slot:label>
                {{ $t('region') }} <span v-if="isEditMode" class="text-danger">*</span>
              </template>
              <b-form-select id="regionID" :disabled="!isEditMode" v-model="planta.regionID" @change="onRegionChange"
                required>
                <option :value="null" disabled>{{ $t('seleccione_opcion') }}</option>
                <option v-for="option in regionOptions" :key="option.value" :value="option.value">
                  {{ option.text }}
                </option>
              </b-form-select>
            </b-form-group>
            <b-form-group label-for="ciudadID">
              <template v-slot:label>
                {{ $t('ciudad') }} <span v-if="isEditMode" class="text-danger">*</span>
              </template>
              <b-form-select id="ciudadID" :disabled="!isRegionSelected || !isEditMode" v-model="planta.ciudadID"
                required>
                <option :value="null" disabled>{{ $t('seleccione_opcion') }}</option>
                <option v-for="option in ciudadOptions" :key="option.value" :value="option.value">
                  {{ option.text }}
                </option>
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>

      <b-form id="form-pasillos" @submit.prevent="submitFormPasillos">
        <b-row v-if="isEditMode" class="mb-3">
          <b-col md="2">
            <b-button :disabled="!isEditMode" @click="agregarPasillo" variant="success">
              {{ $t('agregar_pasillo') }}
            </b-button>
          </b-col>
          <b-col>
            <b-button @click="quitarPasillo" variant="danger" :disabled="planta.pasillos.length === 1">
              {{ $t('quitar_pasillo') }}
            </b-button>
          </b-col>
        </b-row>

        <b-row class="flex-wrap">
          <b-col v-if="planta.pasillos" v-for="(pasillo, index) in planta.pasillos" :key="index" md="2" class="mt-4">
            <b-form-group :label="`${$t('pasillo')} #${index + 1}`" :label-for="'pasillo-' + index">
              <b-form-input :disabled="!isEditMode" v-model="pasillo.nombre" :id="'pasillo-' + index" required
                :placeholder="$t('ingrese_nombre_pasillo')"></b-form-input>
            </b-form-group>

            <span class="ingresos-label">{{ $t('ingresos_origen') }}:</span>
            <span class="ingresos-list">
              <span v-if="pasillo.ingresosOrigen && pasillo.ingresosOrigen.length > 0">
                <span v-for="(ingresoId, i) in pasillo.ingresosOrigen" :key="`origen-${ingresoId}`">
                  <router-link :to="`/ingresos/details/${ingresoId}`" class="ingreso-link">
                    {{ ingresoId }}
                  </router-link>
                  <span v-if="i < pasillo.ingresosOrigen.length - 1">, </span>
                </span>
              </span>
            </span>

            <br />
            <span class="ingresos-label">{{ $t('ingresos_destino') }}:</span>
            <span class="ingresos-list">
              <span v-if="pasillo.ingresosDestino && pasillo.ingresosDestino.length > 0">
                <span v-for="(ingresoId, i) in pasillo.ingresosDestino" :key="`destino-${ingresoId}`">
                  <router-link :to="`/ingresos/details/${ingresoId}`" class="ingreso-link">
                    {{ ingresoId }}
                  </router-link>
                  <span v-if="i < pasillo.ingresosDestino.length - 1">, </span>
                </span>
              </span>
            </span>

          </b-col>
        </b-row>

        <b-row v-if="isEditMode" class="mt-4">
          <b-col>
            <b-button type="submit" :disabled="!isEditMode" v-if="planta.pasillos.length > 0" variant="primary" class="ml-2">
              {{ $t('guardar') }} {{ $t('pasillos') }}
            </b-button>
          </b-col>
        </b-row>
      </b-form>


    </base-header>
  </div>
</template>
<script>
import plantaService from '@/api/plantaService';
import miscService from '@/api/miscService';
export default {
  data() {
    return {
      isEditMode: false,
      planta: {},
      paisOptions: [],
      ciudadOptions: [],
      empresaOptions: [],
      regionOptions: [],
      isPaisSelected: false,
      isRegionSelected: false,
      pasillos: [
        { PasilloID:0, nombre: '' }
      ],
    };
  },
  async beforeMount() {
    this.paisOptions = await miscService.getPaisesOptions();
    await this.initData();
  },
  methods: {
    agregarPasillo() {
      this.planta.pasillos.push({ nombre: '' });
    },
    quitarPasillo() {
      if (this.planta.pasillos.length > 1) {
        this.planta.pasillos.pop();
      }
    },
    async initData() {
      const id = this.$route.params.id;
      this.planta = await plantaService.get(id);

      if (this.planta && this.planta.paisID > 0) {
        this.empresaOptions = await miscService.getEmpresasByPaisOptions(this.planta.paisID);
        this.regionOptions = await miscService.getRegionesOptions(this.planta.paisID);
        this.isPaisSelected = this.isEditMode;
      }

      if (this.planta && this.planta.regionID > 0) {
        this.ciudadOptions = await miscService.getCiudadesOptions(this.planta.regionID);
        this.isRegionSelected = true;
      }
      if (this.planta.pasillos && this.planta.pasillos.length == 0)
        this.planta.pasillos = this.pasillos;
    },
    async cancelBtn() {
      if (this.isEditMode)
        this.initData();
      this.isEditMode = !this.isEditMode;
    },
    async onPaisChange() {
      const pais = this.planta.paisID;
      this.planta.empresaID = null;
      this.planta.regionID = null;
      this.planta.ciudadID = null;
      if (pais) {
        this.empresaOptions = await miscService.getEmpresasByPaisOptions(pais);
        this.regionOptions = await miscService.getRegionesOptions(pais);
        this.isPaisSelected = true;
      } else {
        this.empresaOptions = [];
        this.regionOptions = [];
        this.isPaisSelected = false;
      }
    },
    async onRegionChange() {
      const region = this.planta.regionID;
      this.planta.ciudadID = null;
      if (region) {
        this.ciudadOptions = await miscService.getCiudadesOptions(region);
        this.isRegionSelected = true;
      } else {
        this.ciudadOptions = [];
        this.isRegionSelected = false;
      }
    },
    async submitForm() {
      Swal.fire({
        title: this.$t('estas_seguro'),
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: this.$t('aceptar'),
        denyButtonText: this.$t('cancelar'),
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            Swal.fire({
              html: `<div style="display: flex; align-items: center; justify-content: center;"> 
                      <img src="/img/others/loading.gif" alt="Loading" style="width: 50px; height: 50px; margin-right: 15px;">
                        <div>
                            <h3 style="color: #006393;">${this.$t('guardando')}</h3>
                        </div>
                    </div>`,
              showConfirmButton: false,
              allowOutsideClick: false
            });
            const response = await plantaService.update(this.planta, this.planta.id);
            if (!response) throw new Error(this.$t('error_guardar'));
            this.planta = await plantaService.get(this.planta.id);
            this.isEditMode = false;
            Swal.fire({
              title: this.$t('cambios_guardados'),
              icon: "success",
              showConfirmButton: false,
              timer: 2000,
              background: '#f8f9fa',
              customClass: {
                title: 'swal-title',
                icon: 'swal-icon',
              },
            });
          } catch (error) {
            Swal.fire({
              icon: "error",
              title: "Oops...",
              text: this.$t('error_2')
            });
          }
        } else if (result.isDenied) {
          Swal.close();
        }
      });
    },
    async submitFormPasillos() {
      Swal.fire({
        title: this.$t('estas_seguro'),
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: this.$t('aceptar'),
        denyButtonText: this.$t('cancelar'),
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            Swal.fire({
              html: `<div style="display: flex; align-items: center; justify-content: center;"> 
                      <img src="/img/others/loading.gif" alt="Loading" style="width: 50px; height: 50px; margin-right: 15px;">
                        <div>
                            <h3 style="color: #006393;">${this.$t('guardando')}</h3>
                        </div>
                    </div>`,
              showConfirmButton: false,
              allowOutsideClick: false
            });
            const pasillos = this.planta.pasillos
            const response = await plantaService.updatePasillos(pasillos, this.planta.id, this.planta.empresaID);
            if (!response) throw new Error(this.$t('error_guardar'));
            this.planta = await plantaService.get(this.planta.id);
            this.isEditMode = false;
            Swal.fire({
              title: this.$t('cambios_guardados'),
              icon: "success",
              showConfirmButton: false,
              timer: 2000,
              background: '#f8f9fa',
              customClass: {
                title: 'swal-title',
                icon: 'swal-icon',
              },
            });
          } catch (error) {
            Swal.fire({
              icon: "error",
              title: "Oops...",
              text: this.$t('error_2')
            });
          }
        } else if (result.isDenied) {
          Swal.close();
        }
      });
    },
    validarFormulario() {
      for (const pasillo of this.form.pasillos) {
        if (!pasillo.nombre.trim()) {
          Swal.fire({ icon: 'warning', title: this.$t('nombre_requerido') });
          return false;
        }
      }
      return true;
    }
  },
};
</script>
<style>
.el-table.table-dark {
  background-color: #4d4c4c;
  color: #f8f9fe;
}

.el-table.table-dark th,
.el-table.table-dark tr {
  background-color: #4d4c4c;
}

.el-table.table-dark td,
.el-table.table-dark th.is-leaf {
  border-bottom: none;
}

.ingresos-label {
  font-weight: bold;
  color: #5ba3c5;
}

.ingresos-list {
  display: inline;
}

.ingreso-link {
  color: #5ba3c5;
  text-decoration: none;
}

.ingreso-link:hover {
  text-decoration: underline;
}
</style>
